import React from 'react';
import {
  AlertTriangleIcon,
  ClockIcon,
  FolderIcon,
  TimerIcon,
  PaperclipIcon,
} from 'lucide-react';
import { Badge } from '@components/atoms/Badge';
import { useTranslation } from '@hooks/useTranslation';

interface IncidentCardProps {
  name: string;
  description?: string;
  date: string;
  type: string;
  /**
   * Sprint 28 change request (global sweep, defect fix). Pre-translated by the caller
   * (`incidents.tsx`, which already pre-formats `type` the same way) — this list only ever
   * shows pending incidents, so the badge used to hardcode the raw English word `'PENDING'`.
   */
  statusLabel: string;
  projectName?: string;
  delay?: string;
  documentsCount?: number;
  onClick: () => void;
  onAcknowledge: () => void;
}

export function IncidentCard({
  name,
  description,
  date,
  type,
  statusLabel,
  projectName,
  delay,
  documentsCount,
  onClick,
  onAcknowledge,
}: IncidentCardProps) {
  const tIncidents = useTranslation('incidents');
  return (
    <div
      className="bg-white rounded-lg border border-red-200 p-4 shadow-sm cursor-pointer hover:shadow-md hover:border-red-300 transition-all duration-200"
      onClick={onClick}>
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1 min-w-0">
            <AlertTriangleIcon
              size={20}
              className="text-red-500 mr-2 mt-0.5 flex-shrink-0"
            />
            <h2 className="text-lg font-medium text-gray-900 truncate">
              {name}
            </h2>
          </div>
          <div className="flex gap-2 ml-3 flex-shrink-0">
            <Badge variant="warning" size="sm">
              {statusLabel}
            </Badge>
            <Badge variant="secondary" size="sm">
              {type}
            </Badge>
          </div>
        </div>

        {/* Description */}
        {description && (
          <p className="text-sm text-gray-600 line-clamp-2">{description}</p>
        )}

        {/* Info row */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <ClockIcon size={14} />
            <span>{date}</span>
          </div>
          {projectName && (
            <div className="flex items-center gap-1.5">
              <FolderIcon size={14} />
              <span>{projectName}</span>
            </div>
          )}
          {delay && delay !== 'N/A' && (
            <div className="flex items-center gap-1.5 text-red-500 font-medium">
              <TimerIcon size={14} />
              <span>{delay}</span>
            </div>
          )}
          {documentsCount !== undefined && documentsCount > 0 && (
            <div className="flex items-center gap-1.5">
              <PaperclipIcon size={14} />
              <span>
                {(documentsCount === 1 ? tIncidents.documentCountOne : tIncidents.documentCountOther).replace(
                  '{n}',
                  String(documentsCount),
                )}
              </span>
            </div>
          )}
        </div>

        {/* Acknowledge button */}
        <button type="button"
          className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md transition-colors text-sm font-medium"
          onClick={(e) => {
            e.stopPropagation();
            onAcknowledge();
          }}>
          Acknowledge
        </button>
      </div>
    </div>
  );
}
